import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabase_client';
import { useCompany } from '../contexts/CompanyContext';
import { useSession } from '../contexts/SessionContext';
import { MainLayout } from './MainLayout';
import '../styles/page-base.css';

interface ApiKey {
  id: string;
  key_prefix: string;
  key_suffix: string;
  status: 'active' | 'revoked' | 'expired';
  created_at: string;
  last_used_at: string | null;
  expires_at: string | null;
  deleted_at: string | null;
}

type IntegrationTab = 'api_keys' | 'webhook_endpoints' | 'request_logs';
type FeedbackModal = { type: 'success' | 'error'; title: string; message: string } | null;
type LogFilter = 'all' | '2xx' | '4xx' | '5xx';

interface WebhookRequestLog {
  id: string;
  created_at: string;
  event_type: string;
  status: string;
  attempt_count: number;
  last_error: string | null;
  http_status_code: number | null;
  target_url: string;
  payload: Record<string, any>;
  next_retry_at: string | null;
  sent_at: string | null;
}

export default function PartnerIntegration() {
  const { companyId } = useParams<{ companyId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { selectedCompany } = useCompany();
  const activeOrgId = selectedCompany?.company_id || companyId || null;
  const { permissions, activeOrg } = useSession();
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [copiedKeyId, setCopiedKeyId] = useState<string | null>(null);
  const [deletingKeyId, setDeletingKeyId] = useState<string | null>(null);
  const [, setNewlyGeneratedKey] = useState<string | null>(null);
  const [showGenerateKeyConfirm, setShowGenerateKeyConfirm] = useState(false);
  const [showDeleteKeyConfirm, setShowDeleteKeyConfirm] = useState<string | null>(null);
  const [webhookUrl, setWebhookUrl] = useState('');
  const [isSavingWebhook, setIsSavingWebhook] = useState(false);
  const [isTestingWebhook, setIsTestingWebhook] = useState(false);
  const [feedbackModal, setFeedbackModal] = useState<FeedbackModal>(null);
  const [requestLogs, setRequestLogs] = useState<WebhookRequestLog[]>([]);
  const [isLoadingRequestLogs, setIsLoadingRequestLogs] = useState(false);
  const [requestLogFilter, setRequestLogFilter] = useState<LogFilter>('all');
  const [selectedRequestLog, setSelectedRequestLog] = useState<WebhookRequestLog | null>(null);
  const [activeTab, setActiveTab] = useState<IntegrationTab>(() => {
    const tab = searchParams.get('tab');
    if (tab === 'webhook_endpoints' || tab === 'request_logs') return tab;
    return 'api_keys';
  });
  
  const fetchApiKeys = useCallback(async () => {
    try {
      if (!activeOrgId) {
        setApiKeys([]);
        setIsLoading(false);
        return;
      }
  
      setIsLoading(true);
  
      const { data: orgData, error: orgError } = await supabase
        .from('organizations')
        .select('is_partner, id, name, webhook_url')
        .eq('id', activeOrgId)
        .single();
  
      if (orgError) throw orgError;
  
      if (!orgData?.is_partner) {
        setApiKeys([]);
        setWebhookUrl('');
        setIsLoading(false);
        return;
      }

      setWebhookUrl(orgData.webhook_url || '');
  
      const { data: keysData, error: keysError } = await supabase
        .from('api_keys')
        .select('id, key_prefix, key_suffix, status, created_at, last_used_at, expires_at, deleted_at')
        .eq('org_id', activeOrgId)
        .is('deleted_at', null)
        .order('created_at', { ascending: false });
  
      if (keysError) throw keysError;
      setApiKeys(keysData || []);
    } catch (error) {
      console.error('Error fetching API keys:', error);
    } finally {
      setIsLoading(false);
    }
  }, [activeOrgId]);

  const generateApiKey = async () => {
    setShowGenerateKeyConfirm(true);
  }

  const confirmGenerateApiKey = async () => {
    try {
      setIsGenerating(true);
      
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        openFeedbackModal('error', 'Login Required', 'Please log in first.');
        return;
      }

      const response = await fetch(
        `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/generate-api-key`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
            'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY || ''
          },
          body: JSON.stringify({
            org_id: activeOrgId
          })
        }
      );

      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.error || 'Failed to generate API key');
      }

      // Store in sessionStorage so it persists until tab closes or new key generated
      sessionStorage.setItem(`api_key_${activeOrgId}`, result.api_key);
      setNewlyGeneratedKey(result.api_key);
      
      await fetchApiKeys();
      
      openFeedbackModal('success', 'API Key Generated', 'Copy it now before your session changes.');
    } catch (error: any) {
      console.error('Error generating API key:', error);
      openFeedbackModal('error', 'Generate API Key Failed', `Failed to generate API key: ${error.message}`);
    } finally {
      setIsGenerating(false);
      setShowGenerateKeyConfirm(false);
    }
  };

  useEffect(() => {
    if (activeOrgId) {
      fetchApiKeys();
    }
  }, [activeOrgId, fetchApiKeys]);

  useEffect(() => {
    const storedKey = sessionStorage.getItem(`api_key_${activeOrgId}`);
    if (storedKey) {
      setNewlyGeneratedKey(storedKey);
    }
  }, [activeOrgId]);

  const copyApiKey = async (keyPrefix: string, keySuffix: string, keyId: string) => {
    const fullKey = `${keyPrefix}_${keySuffix}`;
    
    // Check if this is a newly generated key stored in sessionStorage
    const storedKey = sessionStorage.getItem(`api_key_${activeOrgId}`);
    const keyToCopy = storedKey && storedKey.includes(keySuffix) ? storedKey : fullKey;
    
    try {
      await navigator.clipboard.writeText(keyToCopy);
      setCopiedKeyId(keyId);
      setTimeout(() => setCopiedKeyId(null), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
      openFeedbackModal('error', 'Copy Failed', 'Failed to copy API key.');
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString();
  };

  const getStatusBadge = (status: string) => {
    const baseClasses = "px-2 py-1 text-xs font-semibold rounded-full";
    switch (status) {
      case 'active':
        return <span className={`${baseClasses} bg-green-100 text-green-800`}>Active</span>;
      case 'revoked':
        return <span className={`${baseClasses} bg-red-100 text-red-800`}>Revoked</span>;
      case 'expired':
        return <span className={`${baseClasses} bg-gray-100 text-gray-800`}>Expired</span>;
      default:
        return <span className={`${baseClasses} bg-gray-100 text-gray-800`}>{status}</span>;
    }
  };

  const isKeyExpired = (expiresAt: string | null) => {
    if (!expiresAt) return false;
    return new Date(expiresAt) < new Date();
  };

  const deleteApiKey = async (keyId: string) => {
    setShowDeleteKeyConfirm(keyId);
  }

  const confirmDeleteApiKey = async (keyId: string) => {
    try {
      setDeletingKeyId(keyId);
      
      // Only set deleted_at to hide from list, don't change status
      // This keeps the record in database but hides it from UI
      const { error } = await supabase
        .from('api_keys')
        .update({ 
          deleted_at: new Date().toISOString()
        })
        .eq('id', keyId)
        .is('deleted_at', null); // Only update if not already deleted

      if (error) throw error;

      await fetchApiKeys();
      openFeedbackModal('success', 'API Key Removed', 'API key removed from list successfully.');
    } catch (error: any) {
      console.error('Error deleting API key:', error);
      openFeedbackModal('error', 'Remove API Key Failed', `Failed to remove API key: ${error.message}`);
    } finally {
      setDeletingKeyId(null);
      setShowDeleteKeyConfirm(null);
    }
  };

  const getFullApiKey = (keyPrefix: string, keySuffix: string): string => {
    const storedKey = sessionStorage.getItem(`api_key_${activeOrgId}`);
    
    // If this is a newly generated key, return the stored full key
    if (storedKey && storedKey.includes(keySuffix)) {
      return storedKey;
    }
    
    // Otherwise, reconstruct from prefix and suffix
    return `${keyPrefix}_${keySuffix}`;
  };

  const saveWebhookUrl = async () => {
    if (!activeOrgId) return;

    const normalizedWebhookUrl = webhookUrl.trim();
    if (normalizedWebhookUrl) {
      try {
        new URL(normalizedWebhookUrl);
      } catch {
        openFeedbackModal('error', 'Invalid Webhook URL', 'Please enter a valid webhook URL (including http:// or https://).');
        return;
      }
    }

    try {
      setIsSavingWebhook(true);

      const { error } = await supabase
        .from('organizations')
        .update({ webhook_url: normalizedWebhookUrl || null })
        .eq('id', activeOrgId);

      if (error) throw error;
      setWebhookUrl(normalizedWebhookUrl);
      openFeedbackModal(
        'success',
        'Webhook Updated',
        normalizedWebhookUrl ? 'Webhook URL saved successfully.' : 'Webhook URL cleared successfully.'
      );
    } catch (error: any) {
      console.error('Error saving webhook URL:', error);
      openFeedbackModal('error', 'Save Webhook Failed', `Failed to save webhook URL: ${error.message}`);
    } finally {
      setIsSavingWebhook(false);
    }
  };

  const clearWebhookUrl = async () => {
    if (!activeOrgId) return;

    try {
      setIsSavingWebhook(true);

      const { error } = await supabase
        .from('organizations')
        .update({ webhook_url: null })
        .eq('id', activeOrgId);

      if (error) throw error;
      setWebhookUrl('');
      openFeedbackModal('success', 'Webhook Cleared', 'Webhook URL cleared successfully.');
    } catch (error: any) {
      console.error('Error clearing webhook URL:', error);
      openFeedbackModal('error', 'Clear Webhook Failed', `Failed to clear webhook URL: ${error.message}`);
    } finally {
      setIsSavingWebhook(false);
    }
  };

  const testWebhookEndpoint = async () => {
    if (!activeOrgId) return;
    if (!webhookUrl.trim()) {
      openFeedbackModal('error', 'Webhook URL Missing', 'Please save a webhook URL before testing.');
      return;
    }

    try {
      setIsTestingWebhook(true);
      const { data, error } = await supabase.functions.invoke('test-webhook-endpoint', {
        body: { org_id: activeOrgId }
      });

      if (error) {
        throw new Error(error.message || 'Failed to call test webhook endpoint.');
      }

      if (!data?.success) {
        openFeedbackModal(
          'error',
          'Webhook Test Failed',
          data?.error || 'Test request failed.'
        );
        return;
      }

      openFeedbackModal(
        'success',
        'Webhook Test Sent',
        `Test event sent successfully. HTTP ${data.http_status ?? 200}.`
      );
    } catch (error: any) {
      console.error('Error testing webhook endpoint:', error);
      openFeedbackModal('error', 'Webhook Test Failed', error.message || 'Unable to test webhook endpoint.');
    } finally {
      setIsTestingWebhook(false);
      fetchRequestLogs();
    }
  };

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab === 'webhook_endpoints' || tab === 'request_logs' || tab === 'api_keys') {
      setActiveTab(tab);
      return;
    }
    setActiveTab('api_keys');
  }, [searchParams]);

  const changeTab = (tab: IntegrationTab) => {
    setActiveTab(tab);
    setSearchParams({ tab });
  };

  const openFeedbackModal = (type: 'success' | 'error', title: string, message: string) => {
    setFeedbackModal({ type, title, message });
  };

  const parseHttpStatusCode = (status: string, lastError: string | null): number | null => {
    if (status === 'success') return 200;
    if (!lastError) return null;
    const match = lastError.match(/HTTP\s+(\d{3})/i);
    return match ? Number(match[1]) : null;
  };

  const fetchRequestLogs = useCallback(async () => {
    if (!activeOrgId) {
      setRequestLogs([]);
      return;
    }

    try {
      setIsLoadingRequestLogs(true);
      const { data, error } = await supabase
        .from('webhook_events')
        .select('id, created_at, event_type, status, attempt_count, last_error, target_url, payload, next_retry_at, sent_at')
        .eq('org_id', activeOrgId)
        .order('created_at', { ascending: false })
        .limit(100);

      if (error) throw error;

      const mappedLogs: WebhookRequestLog[] = (data || []).map((row: any) => ({
        id: row.id,
        created_at: row.created_at,
        event_type: row.event_type || 'unknown',
        status: row.status || 'pending',
        attempt_count: Number(row.attempt_count || 0),
        last_error: row.last_error || null,
        http_status_code: parseHttpStatusCode(row.status || 'pending', row.last_error || null),
        target_url: row.target_url || '',
        payload: (row.payload && typeof row.payload === 'object') ? row.payload : {},
        next_retry_at: row.next_retry_at || null,
        sent_at: row.sent_at || null,
      }));

      setRequestLogs(mappedLogs);
    } catch (error: any) {
      console.error('Error fetching request logs:', error);
      openFeedbackModal('error', 'Load Request Logs Failed', `Failed to load request logs: ${error.message}`);
    } finally {
      setIsLoadingRequestLogs(false);
    }
  }, [activeOrgId]);

  useEffect(() => {
    if (activeTab === 'request_logs') {
      fetchRequestLogs();
    } else {
      setSelectedRequestLog(null);
    }
  }, [activeTab, fetchRequestLogs]);

  const getEndpointPath = (url: string) => {
    if (!url) return '-';
    try {
      return new URL(url).pathname || '/';
    } catch {
      return url;
    }
  };

  const toPrettyJson = (value: unknown) => {
    try {
      return JSON.stringify(value ?? {}, null, 2);
    } catch {
      return '{}';
    }
  };

  if (isLoading) {
    return (
      <MainLayout>
        <div className="page-container">
          <div className="max-w-6xl mx-auto">
            <div className="text-center py-8">Loading...</div>
          </div>
        </div>
      </MainLayout>
    );
  }

  // Check permission instead of is_partner
  if (!permissions['settings_api_key']) {
    return (
      <MainLayout>
        <div className="page-container">
          <div className="max-w-6xl mx-auto">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <h2 className="text-lg font-semibold text-yellow-800 mb-2">
                Access Denied
              </h2>
              <p className="text-yellow-700">
                You don't have permission to view integration. Only owners can access this section.
              </p>
            </div>
          </div>
        </div>
      </MainLayout>
    );
  }

  // Integration is not available for Singapore organizations
  if (activeOrg?.countryIso === 'SG') {
    return (
      <MainLayout>
        <div className="page-container">
          <div className="max-w-6xl mx-auto">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <h2 className="text-lg font-semibold text-yellow-800 mb-2">
                Integration Not Available
              </h2>
              <p className="text-yellow-700">
                Integration is currently available for Malaysia organizations only.
              </p>
            </div>
          </div>
        </div>
      </MainLayout>
    );
  }

  const filteredRequestLogs = requestLogs.filter((log) => {
    if (requestLogFilter === 'all') return true;
    if (!log.http_status_code) return false;
    if (requestLogFilter === '2xx') return log.http_status_code >= 200 && log.http_status_code < 300;
    if (requestLogFilter === '4xx') return log.http_status_code >= 400 && log.http_status_code < 500;
    if (requestLogFilter === '5xx') return log.http_status_code >= 500 && log.http_status_code < 600;
    return true;
  });

  return (
    <MainLayout>
      <div className="page-container">
        <div className="max-w-6xl mx-auto space-y-6">
          <div className="page-header">
            <div className="page-header-left">
              <h1 className="page-title">Developers</h1>
            </div>
            <div className="page-header-right">
              <button
                onClick={() => navigate(`/integration/${activeOrgId}/docs`)}
                className="f-btn f-btn-success"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                <span>API Documentation</span>
              </button>
            </div>
          </div>

          <div className="page-tabs">
            <nav className="-mb-px flex space-x-8" aria-label="Integration tabs">
              <button
                onClick={() => changeTab('api_keys')}
                className={`group inline-flex items-center py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'api_keys'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                API Keys
              </button>
              <button
                onClick={() => changeTab('webhook_endpoints')}
                className={`group inline-flex items-center py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'webhook_endpoints'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Webhook Endpoints
              </button>
              <button
                onClick={() => changeTab('request_logs')}
                className={`group inline-flex items-center py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'request_logs'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Request Logs
              </button>
            </nav>
          </div>

          {/* API Keys tab */}
          {activeTab === 'api_keys' && (
          <div className="company-profile-card">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-blue-600">API Keys</h2>
              <button
                onClick={generateApiKey}
                disabled={isGenerating}
                className="f-btn f-btn-primary"
              >
                {isGenerating ? 'Generating...' : 'Generate New API Key'}
              </button>
            </div>

            {apiKeys.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-600 mb-4">No API keys found. Generate one to get started.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="page-table">
              <thead className="page-table-thead">
                <tr>
                  <th className="page-table-th">
                    API Key
                  </th>
                  <th className="page-table-th">
                    Status
                  </th>
                  <th className="page-table-th">
                    Created At
                  </th>
                  <th className="page-table-th">
                    Last Used
                  </th>
                  <th className="page-table-th">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="page-table-tbody">
                {apiKeys.map((key) => {
                  const fullKey = getFullApiKey(key.key_prefix, key.key_suffix);
                  const isExpired = isKeyExpired(key.expires_at);
                  const displayStatus = isExpired ? 'expired' : key.status;
                  const isCopied = copiedKeyId === key.id;
                  
                  return (
                    <tr key={key.id} className={key.status === 'active' ? 'bg-white' : 'bg-gray-50'}>
                      <td className="page-table-td">
                        <div className="flex items-center gap-2">
                          <code className="text-sm font-mono text-gray-800 break-all">
                            {fullKey}
                          </code>
                          <button
                            onClick={() => copyApiKey(key.key_prefix, key.key_suffix, key.id)}
                            className="flex-shrink-0 p-1 hover:bg-gray-200 rounded transition"
                            title="Copy API key"
                          >
                            {isCopied ? (
                              <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                              </svg>
                            ) : (
                              <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                              </svg>
                            )}
                          </button>
                        </div>
                      </td>
                      <td className="page-table-td">
                        {getStatusBadge(displayStatus)}
                      </td>
                      <td className="page-table-td">
                        {formatDate(key.created_at)}
                      </td>
                      <td className="page-table-td">
                        {formatDate(key.last_used_at)}
                      </td>
                      <td className="page-table-td">
                        <button
                          onClick={() => deleteApiKey(key.id)}
                          disabled={deletingKeyId === key.id || key.deleted_at !== null}
                          className="f-btn f-btn-danger f-btn-xs"
                          title="Delete API key from list"
                        >
                          {deletingKeyId === key.id ? 'Deleting...' : 'Delete'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
                </table>
              </div>
            )}
          </div>
          )}

          {/* Webhook Endpoints tab */}
          {activeTab === 'webhook_endpoints' && (
          <div className="company-profile-card">
            <div className="mb-4 flex items-start justify-between gap-3">
              <div>
                <h2 className="text-xl font-bold text-blue-600">Webhook Endpoints</h2>
                <p className="text-sm text-gray-600 mt-1">
                  Configure where we should send filing status updates.
                </p>
              </div>
              <button
                type="button"
                onClick={testWebhookEndpoint}
                disabled={isSavingWebhook || isTestingWebhook}
                className="f-btn f-btn-soft"
              >
                {isTestingWebhook ? 'Testing...' : 'Test Endpoint'}
              </button>
            </div>

            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Webhook URL
                </label>
                <input
                  type="url"
                  value={webhookUrl}
                  onChange={(e) => setWebhookUrl(e.target.value)}
                  placeholder="https://your-domain.com/webhooks/filing-status"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className="mt-4 space-y-3">
              <div className="flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={clearWebhookUrl}
                  disabled={isSavingWebhook || isTestingWebhook}
                  className="f-btn f-btn-danger"
                >
                  Clear
                </button>
                <button
                  type="button"
                  onClick={saveWebhookUrl}
                  disabled={isSavingWebhook || isTestingWebhook}
                  className="f-btn f-btn-primary"
                >
                  {isSavingWebhook ? 'Saving...' : 'Save Webhook URL'}
                </button>
              </div>

              <div className="flex items-center justify-between">
                <p className="text-xs text-gray-500">
                  We send a webhook to this URL whenever filing processing is completed or failed.
                </p>
              </div>
            </div>
          </div>
          )}

          {/* Request Logs tab */}
          {activeTab === 'request_logs' && (
            <div className="company-profile-card">
              <div className="mb-5">
                <h2 className="text-xl font-bold text-blue-600">Request Logs</h2>
                <p className="text-sm text-gray-600 mt-1">
                  Monitor API request history for your integration.
                </p>
              </div>

              <div className="flex items-center gap-2 mb-4">
                {[
                  { key: 'all', label: 'All' },
                  { key: '2xx', label: '2xx' },
                  { key: '4xx', label: '4xx' },
                  { key: '5xx', label: '5xx' },
                ].map((chip) => (
                  <button
                    key={chip.key}
                    type="button"
                    onClick={() => setRequestLogFilter(chip.key as LogFilter)}
                    className={`px-3 py-1 text-xs rounded-full border ${
                      requestLogFilter === chip.key
                        ? 'bg-gray-100 border-gray-200 text-gray-700'
                        : 'bg-white border-gray-200 text-gray-500'
                    }`}
                  >
                    {chip.label}
                  </button>
                ))}
              </div>

              <div className="overflow-x-auto border border-gray-100 rounded-lg">
                <table className="page-table">
                  <thead className="page-table-thead">
                    <tr>
                      <th className="page-table-th">Date</th>
                      <th className="page-table-th">HTTP Status Code</th>
                      <th className="page-table-th">Method</th>
                      <th className="page-table-th">Endpoint</th>
                      <th className="page-table-th">Attempts</th>
                    </tr>
                  </thead>
                  <tbody className="page-table-tbody">
                    {!isLoadingRequestLogs && filteredRequestLogs.map((log) => (
                      <tr
                        key={log.id}
                        className={`cursor-pointer ${selectedRequestLog?.id === log.id ? 'bg-blue-50' : ''}`}
                        onClick={() => setSelectedRequestLog(log)}
                      >
                        <td className="page-table-td">{formatDate(log.created_at)}</td>
                        <td className="page-table-td">
                          {log.http_status_code ? log.http_status_code : (log.status === 'pending' ? 'Pending' : '-')}
                        </td>
                        <td className="page-table-td">POST</td>
                        <td className="page-table-td">{getEndpointPath(log.target_url)}</td>
                        <td className="page-table-td">{log.attempt_count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {isLoadingRequestLogs && (
                  <div className="py-16 text-center">
                    <p className="text-sm text-gray-500">Loading request logs...</p>
                  </div>
                )}
                {!isLoadingRequestLogs && filteredRequestLogs.length === 0 && (
                  <div className="py-16 text-center">
                    <p className="text-base font-semibold text-gray-800">No data to display</p>
                    <p className="text-sm text-gray-500 mt-1">There are no request logs available.</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Generate API Key Confirmation Modal */}
          {showGenerateKeyConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Generate API Key</h3>
            <p className="text-gray-600 mb-6">
              {apiKeys.some(k => k.status === 'active')
                ? 'Generating a new API key will revoke all existing active keys. Continue?'
                : 'Generate a new API key?'}
            </p>
            <div className="flex items-center justify-end space-x-3">
              <button
                onClick={() => setShowGenerateKeyConfirm(false)}
                className="f-btn f-btn-soft"
              >
                Cancel
              </button>
              <button
                onClick={confirmGenerateApiKey}
                className="f-btn f-btn-success"
                disabled={isGenerating}
              >
                {isGenerating ? 'Generating...' : 'Generate Key'}
              </button>
            </div>
          </div>
        </div>
          )}

          {/* Delete API Key Confirmation Modal */}
          {showDeleteKeyConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Remove API Key</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to remove this API key from the list? The record will be kept for audit purposes.
            </p>
            <div className="flex items-center justify-end space-x-3">
              <button
                onClick={() => setShowDeleteKeyConfirm(null)}
                className="f-btn f-btn-soft"
              >
                Cancel
              </button>
              <button
                onClick={() => confirmDeleteApiKey(showDeleteKeyConfirm)}
                className="f-btn f-btn-danger"
                disabled={deletingKeyId === showDeleteKeyConfirm}
              >
                {deletingKeyId === showDeleteKeyConfirm ? 'Removing...' : 'Remove Key'}
              </button>
            </div>
          </div>
        </div>
          )}

          {selectedRequestLog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
          <div className="bg-white rounded-lg w-full max-w-4xl max-h-[85vh] overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100 flex items-start justify-between">
              <div>
                <p className="text-xs text-gray-500">{formatDate(selectedRequestLog.created_at)}</p>
                <p className="text-base font-semibold text-gray-800 mt-1">
                  {selectedRequestLog.http_status_code ? `HTTP ${selectedRequestLog.http_status_code}` : selectedRequestLog.status.toUpperCase()}
                </p>
              </div>
              <button
                type="button"
                className="text-gray-400 hover:text-gray-700"
                onClick={() => setSelectedRequestLog(null)}
                title="Close details"
              >
                ✕
              </button>
            </div>

            <div className="px-5 py-4 space-y-4 overflow-y-auto max-h-[calc(85vh-76px)]">
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Request</p>
                <p className="text-sm text-gray-600"><strong>Method:</strong> POST</p>
                <p className="text-sm text-gray-600 break-all"><strong>URL:</strong> {selectedRequestLog.target_url || '-'}</p>
                <p className="text-sm text-gray-600"><strong>Event:</strong> {selectedRequestLog.event_type}</p>
              </div>

              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Body</p>
                <pre className="text-xs bg-gray-900 text-gray-100 rounded-md p-3 overflow-x-auto whitespace-pre-wrap">
{toPrettyJson(selectedRequestLog.payload)}
                </pre>
              </div>

              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Response</p>
                <p className="text-sm text-gray-600">
                  <strong>Status:</strong> {selectedRequestLog.http_status_code ? selectedRequestLog.http_status_code : selectedRequestLog.status}
                </p>
                <p className="text-sm text-gray-600">
                  <strong>Attempts:</strong> {selectedRequestLog.attempt_count}
                </p>
                <p className="text-sm text-gray-600">
                  <strong>Sent At:</strong> {selectedRequestLog.sent_at ? formatDate(selectedRequestLog.sent_at) : '-'}
                </p>
                <p className="text-sm text-gray-600">
                  <strong>Next Retry:</strong> {selectedRequestLog.next_retry_at ? formatDate(selectedRequestLog.next_retry_at) : '-'}
                </p>
              </div>

              {selectedRequestLog.last_error && (
                <div>
                  <p className="text-xs font-semibold text-red-600 uppercase tracking-wide mb-2">Error</p>
                  <pre className="text-xs bg-red-50 text-red-700 border border-red-100 rounded-md p-3 overflow-x-auto whitespace-pre-wrap">
{selectedRequestLog.last_error}
                  </pre>
                </div>
              )}
            </div>
          </div>
        </div>
          )}

          {feedbackModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className={`text-lg font-semibold mb-4 ${feedbackModal.type === 'success' ? 'text-green-700' : 'text-red-700'}`}>
              {feedbackModal.title}
            </h3>
            <p className="text-gray-600 mb-6">{feedbackModal.message}</p>
            <div className="flex items-center justify-end">
              <button
                onClick={() => setFeedbackModal(null)}
                className={feedbackModal.type === 'success' ? 'f-btn f-btn-success' : 'f-btn f-btn-danger'}
              >
                OK
              </button>
            </div>
          </div>
        </div>
          )}
        </div>
      </div>
    </MainLayout>
  );
}